/**
 * Primitivitive vizuale pentru consola de platformă (`/admin`).
 *
 * De ce un set separat de componente față de dashboard-ul restaurantelor: consola este o zonă
 * operațională, cu densitate mare de informație și temă închisă, care semnalează clar că
 * utilizatorul a ieșit din spațiul unui restaurant. Tokenii de culoare rămân cei din UI_SYSTEM.
 */
import type { ReactNode } from 'react';
import { Button, Icon, Skeleton, cx } from '../../components/ui.js';
import { useSession } from '../../lib/session.js';

export function AdminShell({ children }: { children: ReactNode }) {
  const session = useSession();
  return (
    <div className="min-h-screen bg-ink-950 text-white">
      <header className="border-b border-white/10 bg-ink-950/95 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-white/10 text-white">
              <Icon name="shield" size={17} />
            </span>
            <div className="flex flex-col leading-none">
              <span className="font-display text-[17px] text-white">Menuvia</span>
              <span className="text-caption text-ink-400">Consola platformei</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {session.user ? (
              <span className="hidden items-center gap-2 text-caption text-ink-200 sm:flex">
                {session.user.email}
              </span>
            ) : null}
            <a href="/admin/operate/menus">
              <Button variant="ghost" size="sm" className="text-ink-200 hover:bg-white/10 hover:text-white">
                Administrare restaurant
              </Button>
            </a>
          </div>
        </div>
      </header>
      {children}
    </div>
  );
}

export function AdminLoading() {
  return (
    <div className="mx-auto grid w-full max-w-7xl gap-4 px-4 py-10 sm:px-6 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 8 }).map((_, index) => (
        <Skeleton key={index} className="h-24 bg-white/5" />
      ))}
    </div>
  );
}

export function AdminCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('rounded-[var(--radius-lg)] border border-white/10 bg-white/[0.03] p-5', className)}>
      {children}
    </div>
  );
}

export function AdminTable({ headers, children }: { headers: string[]; children: ReactNode }) {
  return (
    <div className="-mx-5 overflow-x-auto px-5">
      <table className="w-full min-w-[640px] border-collapse text-left">
        <thead>
          <tr className="border-b border-white/10">
            {headers.map((header, index) => (
              <th
                key={`${header}-${index}`}
                className="pb-2 pr-4 text-caption font-medium uppercase tracking-[0.08em] text-ink-400"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function AdminRow({ children, tone }: { children: ReactNode; tone?: 'danger' }) {
  return (
    <tr className={cx('border-b border-white/5 last:border-0', tone === 'danger' && 'bg-danger/5')}>{children}</tr>
  );
}

export function Td({ children, className }: { children: ReactNode; className?: string }) {
  return <td className={cx('py-3 pr-4 align-middle text-body-s text-ink-200', className)}>{children}</td>;
}

export function AdminHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="flex min-w-0 flex-col gap-1">
        <h1 className="font-display text-[26px] leading-tight text-white">{title}</h1>
        {subtitle ? <p className="text-body-s text-ink-400">{subtitle}</p> : null}
      </div>
      {actions}
    </header>
  );
}

export function DarkKpi({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'neutral' | 'brand';
}) {
  return (
    <AdminCard className={cx('flex flex-col gap-1', tone === 'brand' && 'border-brand-500/40 bg-brand-600/10')}>
      <span className="text-caption uppercase tracking-[0.08em] text-ink-400">{label}</span>
      <span className="font-display text-[28px] leading-none text-white nums">{value}</span>
      {hint ? <span className="text-caption text-ink-400">{hint}</span> : null}
    </AdminCard>
  );
}

export function AdminFilterInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="h-10 w-full rounded-[var(--radius-md)] border border-white/15 bg-white/5 px-3 text-body-s text-white placeholder:text-ink-400 focus:border-brand-500 sm:w-72"
    />
  );
}

export function AdminTableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton key={index} className="h-12 bg-white/5" />
      ))}
    </div>
  );
}
